// posthog-init (web #1) — load + init PostHog on the web client so the
// "Report a problem" feedback modal can capture `$feedback` (same event the
// desktop sends → flows into inbox/alpha-feed/.../DIGEST.md), and so the web
// gets basic funnel/pageview analytics it previously lacked.
//
// The key is the PUBLIC project key (safe to embed in a client bundle),
// injected at build via VITE_PUBLIC_POSTHOG_KEY. When the env var is absent
// (dev / a preview built without it) init no-ops with a warning instead of
// throwing — window.posthog?.capture() callers then silently skip.

// Type-only import — erased entirely at compile time (esbuild/tsc strip
// `import type`), so this does NOT trigger loading the real posthog-js
// module at runtime. Keeps posthogBeforeSend's signature honest against the
// real CaptureResult shape (@posthog/types/dist/capture.d.ts) without
// violating initPosthog's "never load posthog-js under the test runner" rule.
import type { CaptureResult } from 'posthog-js';

let inited = false;

// TWO paths in this product carry their secret IN the path, and for both the
// secret is the whole credential:
//   /invite/<token>  — the invite-preview endpoint promises "never log the
//                      token" (#5);
//   /t/<uuid>        — the words-only setlist link (wave B). This one takes no
//                      login at all, so the uuid is not one factor of the
//                      credential, it IS the credential.
// posthog-js's automatic $pageview/$pageleave capture ships URL-ish properties —
// including a live one of either — under more keys
// than a hand-written allowlist can keep up with. A confirmed adversarial
// re-review (#5) found $prev_pageview_pathname (emitted on $pageleave when
// capture_pageview:true, which defaults capture_pageleave to
// "if_capture_pageview" — the normal accept→/library flow) sailing straight
// through the old ['$current_url','$pathname','$referrer','$referring_domain']
// allowlist untouched. Shape-match instead: any string-valued property whose
// KEY looks url/pathname/referrer-ish gets redacted, regardless of exact
// name. Exported as a pure function for unit testing; wired into
// posthog.init's `before_send` below via posthogBeforeSend (sanitize_properties
// is the older hook but is deprecated in favor of before_send as of the
// installed posthog-js version — see posthog-core.js's SANITIZE_DEPRECATED
// warning).
const URLISH_KEY_RE = /url|pathname|referrer|referring_domain/i;

// Security fix (guest-link key in analytics, #12): the unified setlist's guest
// link is `/s/<setlistId>?g=<guestKey>` — unlike the invite token and the /t/
// uuid above, the SECRET here is not the path, it's the `g` QUERY PARAMETER.
// `setlistId` itself is deliberately left alone (see the /s/ note below): it
// is useless to anyone without the matching `g`, so keeping it lets analytics
// still tell setlists apart.
//
// Three shapes have to be covered, all exercised by real code paths in this
// app (not hypothetical):
//   1. plain  `?g=<key>` / `&g=<key>`               — posthog-js $current_url
//      on the guest screen itself.
//   2. percent-encoded INSIDE another param's value  — route.ts's
//      buildSignInHandler builds `/login?next=<encodeURIComponent(pathname+search)>`,
//      so a reload/guest sign-in prompt ships `next=%2Fs%2F<id>%3Fg%3D<key>`.
//   3. the same plain shape again via text-link-redirect.ts, which
//      `location.replace()`s to `/s/<id>?g=<id>` for legacy /t/ links resolved
//      to a setlist — that's a normal navigation, so it's the plain shape (1),
//      not a new one.
// Matching the parameter name `g` (not a value that merely looks like one)
// exactly is what keeps this from also eating `tag=` or `gg=`: the regexes
// below anchor `g=`/`g%3D` to a preceding `?`/`&` (or `%3F`/`%26`), so a `g`
// that isn't its own parameter name never matches.
const GUEST_KEY_QUERY_RE = /([?&])g=[^&#]*/g;
// %3F='?', %26='&', %3D='=' — case-insensitive because URL-encoders differ on
// hex-digit letter case (%3f vs %3F). The value itself stops at the next
// encoded-or-plain `&`/`#`/end so a following param survives untouched (same
// %2Fs%2F... string can carry more than one param after `next=`).
const GUEST_KEY_QUERY_ENCODED_RE = /(%3F|%26)g%3D(?:(?!%26|%23).)*/gi;

export function sanitizeSecretPaths<T extends Record<string, unknown>>(properties: T): T {
  if (!properties) return properties;
  let changed = false;
  const next: Record<string, unknown> = { ...properties };
  for (const [key, value] of Object.entries(next)) {
    if (typeof value !== 'string' || !URLISH_KEY_RE.test(key)) continue;
    // A fresh regex literal per iteration — URLISH_KEY_RE has no `g` flag so
    // its own .test() has no lastIndex hazard, but this replace regex is kept
    // as a literal here too (not hoisted to module scope) so a future edit
    // adding a `g` flag to it can't reintroduce that hazard across keys.
    // Both path shapes, each with its own literal. The uuid form is matched
    // case-insensitively and exactly 8-4-4-4-12: /t/ followed by anything else is
    // not one of our links (the route itself is strict uuid — router.ts), and
    // redacting by guess would hide real paths instead of secrets.
    // NOT redacted: the `/s/<uuid>` path itself — see the guest-key comment
    // above, setlistId isn't a secret on its own.
    const redacted = value
      .replace(/\/invite\/[^/?#]+/g, '/invite/<redacted>')
      .replace(/\/t\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '/t/<redacted>')
      .replace(GUEST_KEY_QUERY_RE, '$1g=<redacted>')
      .replace(GUEST_KEY_QUERY_ENCODED_RE, '$1g%3D<redacted>');
    if (redacted !== value) {
      next[key] = redacted;
      changed = true;
    }
  }
  return (changed ? next : properties) as T;
}

// The `before_send` hook itself, factored out as a pure, exported function so
// it can be unit-tested without importing posthog-js (banned under the test
// runner — see initPosthog's note). B2 (#5 adversarial re-review): `$set` and
// `$set_once` are TOP-LEVEL SIBLINGS of `properties` on CaptureResult (see
// @posthog/types/dist/capture.d.ts), carrying person-property side-channel
// values like `$set_once.$initial_current_url` — a hook that only touches
// `cr.properties` never sees them. Gated today by person-processing
// (person_profiles: 'identified_only'), but the gate opens on the first
// identify() call or any active feature flag, so this must be handled now,
// not deferred. `before_send` returning null drops the event entirely — a
// null input must stay null, and a real event must never accidentally come
// back null (that would silently drop ALL analytics).
export function posthogBeforeSend(cr: CaptureResult | null): CaptureResult | null {
  if (!cr) return cr;
  return {
    ...cr,
    properties: sanitizeSecretPaths(cr.properties),
    ...(cr.$set ? { $set: sanitizeSecretPaths(cr.$set) } : {}),
    ...(cr.$set_once ? { $set_once: sanitizeSecretPaths(cr.$set_once) } : {}),
  };
}

// ── Owner identity stitching (telemetry audit 2026-07-28) ──────────────────
// share-web never called identify(), so with person_profiles:'identified_only'
// the same human showed up as two unrelated PostHog persons — one from the
// desktop app, one anonymous per browser — and the "published a share → opened
// it in the browser" funnel could never close. Web is our highest-volume event
// source, so the split cost the most where it mattered most.
//
// Called ONLY from the cookie-authed /library surface, where the OTP login has
// already established who this is, with the SAME `users.id` the desktop passes
// to posthog.identify() (app/renderer/alpha-init.js). Anonymous viewers of a
// public share are never identified — we don't know them and shouldn't.
//
// PRIVACY (non-negotiable): the id and NOTHING else. The desktop's identify()
// additionally ships raw email and the tester's real name as person-properties;
// that is a separate open question and this deliberately does not copy it, so
// the web adds zero PII surface. Do not add an email/name "for convenience" —
// posthog-init.test.ts asserts their absence as a contract.
//
// Deliberately routed through `window.posthog?.identify?.()` instead of an
// import: initPosthog must never load posthog-js under the test runner (see its
// note below), so an import here would make this untestable. Same window-hop
// that feedback-modal.ts and promo-modal.ts use for capture(). When no key is
// configured `window.posthog` is simply absent → silent no-op.
let identifiedUserId: string | null = null;

export function identifyOwner(userId: string): void {
  if (!userId) return;
  // posthog-js's own identify() is not free on repeat calls — it re-sends
  // $identify and can log a "different distinct_id" warning — and /library
  // re-renders (post-auth retry, join/leave) would call this several times per
  // page. Guard on the id, not a boolean, so switching accounts still restitches.
  if (identifiedUserId === userId) return;
  const ph = (window as unknown as { posthog?: { identify?: (id: string) => void } }).posthog;
  if (!ph?.identify) return;
  try {
    ph.identify(userId);
    identifiedUserId = userId;
  } catch {
    // Telemetry must never break the app. Leave identifiedUserId unset so a
    // later render can retry.
  }
}

/** Test seam only — clears the once-per-user guard between test cases. */
export function __resetIdentifyForTests(): void {
  identifiedUserId = null;
}

// posthog-js is dynamically imported only when a key is present: its module
// init touches window.location (survey param parsing) and would throw at import
// time in non-browser test envs. Lazy-loading also keeps it out of the initial
// bundle for builds without the key.
export async function initPosthog(): Promise<boolean> {
  if (inited) return true;
  const env = import.meta.env as unknown as Record<string, string | undefined>;
  // Never load posthog-js under the test runner — its survey/exception loaders
  // fetch external deps and throw in happy-dom (and a build-time .env.local can
  // leak the key into the test env). Telemetry isn't under test here.
  if (env.MODE === 'test' || env.VITEST) return false;
  const key = env.VITE_PUBLIC_POSTHOG_KEY;
  if (!key) {
    // eslint-disable-next-line no-console
    console.warn('[posthog] VITE_PUBLIC_POSTHOG_KEY missing — $feedback + analytics disabled on web.');
    return false;
  }
  try {
    const { default: posthog } = await import('posthog-js');
    posthog.init(key, {
      api_host: env.VITE_PUBLIC_POSTHOG_HOST || 'https://eu.i.posthog.com',
      person_profiles: 'identified_only',
      capture_pageview: true,
      before_send: posthogBeforeSend,
    });
    (window as unknown as { posthog: typeof posthog }).posthog = posthog;
    inited = true;
    return true;
  } catch (e) {
    // Telemetry must never break the app.
    // eslint-disable-next-line no-console
    console.warn('[posthog] init failed', e);
    return false;
  }
}
